import { billingReviewApi } from '@/lib/api/billing-review.api'
import { invoiceApi } from '@/lib/api/invoices.api'
import {
  billingScheduleCategoryApi,
  billingScheduleLineApi,
} from '@/modules/billing-schedule/api'

import type {
  BillingRunOutcome,
  BillingRunRequest,
  BillingScheduleLinesResponse,
  ClientBillingReview,
  CreateBillingRunPayload,
  CreateBillingRunResponse,
  UpsertClientBillingReviewInput,
  WorkspaceInvoiceRollupItem,
} from '@scrambl/shared'
import {
  BillingRunOutcomeSchema,
  BillingRunPeriodSchema,
  BillingRunRequestSchema,
  CLIENT_BILLING_REVIEW_STATUS_VALUES,
  CreateBillingRunSchema,
  InvoiceStatusEnum,
  isCalendarDate,
  isValidUUID,
  UpsertClientBillingReviewSchema,
} from '@scrambl/shared'

import {
  assertScheduleFilters,
  billingPreview,
  billingQueue,
  BillingReadContractError,
  billingSchedulePage,
} from './billing-read-contracts'

export interface BillingReaderContext {
  userId: string
  organizationId: string
  signal: AbortSignal
}

export { BillingReadContractError } from './billing-read-contracts'

function assertContext(context: BillingReaderContext) {
  context.signal.throwIfAborted()
  if (![context.userId, context.organizationId].every(isValidUUID))
    throw new BillingReadContractError()
}
function options(context: BillingReaderContext) {
  assertContext(context)
  return {
    signal: context.signal,
    headers: { 'x-organization-id': context.organizationId },
  }
}
function month(value: string): string {
  const period = value.slice(0, 7)
  if (
    !/^\d{4}-\d{2}(?:-\d{2})?$/.test(value) ||
    !isCalendarDate(`${period}-01`) ||
    (value.length > 7 && !isCalendarDate(value))
  )
    throw new BillingReadContractError()
  return period
}
function assertReview(
  row: ClientBillingReview,
  context: BillingReaderContext,
  period: string,
) {
  if (
    !row ||
    row.organizationId !== context.organizationId ||
    ![row.id, row.entityId].every(isValidUUID) ||
    row.periodStart !== `${period}-01` ||
    !CLIENT_BILLING_REVIEW_STATUS_VALUES.includes(row.status) ||
    (row.reviewerUserId !== null && !isValidUUID(row.reviewerUserId)) ||
    (row.dueDate !== null && !isCalendarDate(row.dueDate))
  )
    throw new BillingReadContractError()
}
function assertOwnedRows<T extends { id?: unknown; organizationId?: unknown }>(
  rows: T[],
  context: BillingReaderContext,
): asserts rows is (T & { id: string; organizationId: string })[] {
  if (
    !Array.isArray(rows) ||
    rows.some(
      row =>
        !row ||
        typeof row.id !== 'string' ||
        !isValidUUID(row.id) ||
        row.organizationId !== context.organizationId,
    ) ||
    new Set(rows.map(row => row.id)).size !== rows.length
  )
    throw new BillingReadContractError()
}

function invoiceRollup(
  rows: unknown,
  period: string,
): WorkspaceInvoiceRollupItem[] {
  if (!Array.isArray(rows)) throw new BillingReadContractError()
  const seen = new Set<string>()
  const nullableText = (value: unknown) =>
    value === null || typeof value === 'string'
  return rows.map((row: WorkspaceInvoiceRollupItem) => {
    if (
      !row ||
      ![row.id, row.entityId].every(isValidUUID) ||
      seen.has(row.id) ||
      !nullableText(row.entityName) ||
      typeof row.invoiceNumber !== 'string' ||
      !nullableText(row.title) ||
      !Object.values(InvoiceStatusEnum).includes(
        row.status as InvoiceStatusEnum,
      ) ||
      typeof row.totalAmount !== 'string' ||
      !/^-?\d{1,13}\.\d{2}$/.test(row.totalAmount) ||
      typeof row.currency !== 'string' ||
      !/^[A-Z]{3}$/.test(row.currency) ||
      !isCalendarDate(row.issueDate) ||
      row.issueDate.slice(0, 7) !== period ||
      (row.dueDate !== null && !isCalendarDate(row.dueDate)) ||
      !nullableText(row.source) ||
      !nullableText(row.invoiceKind) ||
      (row.retainerPeriodId !== null && !isValidUUID(row.retainerPeriodId)) ||
      (row.approvalStatus === null
        ? row.approvalRequestId !== null
        : ![
            'PENDING',
            'APPROVED',
            'CHANGES_REQUESTED',
            'CANCELLED',
            'SUPERSEDED',
          ].includes(row.approvalStatus) || !isValidUUID(row.approvalRequestId))
    )
      throw new BillingReadContractError()
    seen.add(row.id)
    // The overview owns these fields; a transport reply cannot seed full
    // invoice bodies or hidden settings into its reader cache.
    return {
      id: row.id,
      entityId: row.entityId,
      entityName: row.entityName,
      invoiceNumber: row.invoiceNumber,
      title: row.title,
      status: row.status,
      totalAmount: row.totalAmount,
      currency: row.currency,
      issueDate: row.issueDate,
      dueDate: row.dueDate,
      source: row.source,
      invoiceKind: row.invoiceKind,
      retainerPeriodId: row.retainerPeriodId,
      approvalStatus: row.approvalStatus,
      approvalRequestId: row.approvalRequestId,
    }
  })
}

/** Selected-workspace billing transport: caller identity and abort stay explicit. */
export const billingReadsApi = {
  async preview(
    entityId: string,
    params: Parameters<typeof invoiceApi.getClientBillingRunPreview>[1],
    context: BillingReaderContext,
  ) {
    if (!isValidUUID(entityId)) throw new BillingReadContractError()
    if (!BillingRunPeriodSchema.safeParse(params).success)
      throw new BillingReadContractError()
    const row = await invoiceApi.getClientBillingRunPreview(
      entityId,
      params,
      options(context),
    )
    assertContext(context)
    return billingPreview(row, entityId, params)
  },
  async charges(
    params: Parameters<typeof invoiceApi.getUnbilledChargesQueue>[0],
    context: BillingReaderContext,
  ) {
    if (!BillingRunPeriodSchema.safeParse(params).success)
      throw new BillingReadContractError()
    const row = await invoiceApi.getUnbilledChargesQueue(
      params,
      options(context),
    )
    assertContext(context)
    return billingQueue(row, params)
  },
  async invoices(period: string, context: BillingReaderContext) {
    const normalized = month(period)
    const rows = await invoiceApi.getWorkspaceRollup(
      normalized,
      options(context),
    )
    assertContext(context)
    return invoiceRollup(rows, normalized)
  },
  async reviews(period: string, context: BillingReaderContext) {
    const normalized = month(period)
    const rows = await billingReviewApi.listForPeriod(
      { period: normalized },
      options(context),
    )
    assertContext(context)
    assertOwnedRows(rows, context)
    rows.forEach(row => assertReview(row, context, normalized))
    if (new Set(rows.map(row => row.entityId)).size !== rows.length)
      throw new BillingReadContractError()
    return rows
  },
  async lines(
    params: Parameters<typeof billingScheduleLineApi.listLines>[0],
    context: BillingReaderContext,
  ) {
    assertScheduleFilters(params)
    const result = await billingScheduleLineApi.listLines(
      params,
      options(context),
    )
    assertContext(context)
    return billingSchedulePage(result, params ?? {}, context.organizationId)
  },
  /** Financial summaries consume the entire filtered set, never the first page.
   * Sequential pages share one captured session; incomplete/changed sets refuse all data. */
  async completeLines(
    params: Parameters<typeof billingScheduleLineApi.listLines>[0],
    context: BillingReaderContext,
  ): Promise<BillingScheduleLinesResponse> {
    const owner = { ...context }
    assertContext(owner)
    const captured = structuredClone(params ?? {})
    assertScheduleFilters(captured)
    if (captured.page !== undefined && captured.page !== 1)
      throw new BillingReadContractError()
    const first = await billingReadsApi.lines(
      { ...captured, page: 1, limit: 500 },
      owner,
    )
    const total = first.meta.totalItems
    if (total > 10000)
      throw new BillingReadContractError(
        'This billing view contains more than 10,000 lines. Narrow the billing period or filters.',
      )
    const data = [...first.data],
      seen = new Set(data.map(line => line.id))
    for (let page = 2; page <= first.meta.totalPages; page++) {
      const next = await billingReadsApi.lines(
        { ...captured, page, limit: 500 },
        owner,
      )
      if (
        next.meta.totalItems !== total ||
        next.meta.totalPages !== first.meta.totalPages
      )
        throw new BillingReadContractError()
      for (const line of next.data) {
        if (seen.has(line.id)) throw new BillingReadContractError()
        seen.add(line.id)
        data.push(line)
      }
    }
    assertContext(owner)
    if (data.length !== total) throw new BillingReadContractError()
    return {
      data,
      meta: {
        currentPage: 1,
        itemsPerPage: Math.max(1, total),
        totalItems: total,
        totalPages: total ? 1 : 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    }
  },

  async categories(context: BillingReaderContext) {
    const rows = await billingScheduleCategoryApi.listCategories(
      options(context),
    )
    assertContext(context)
    assertOwnedRows(rows, context)
    return rows
  },
}

/** Review replies confirm the submitted owner/month; they never seed reader data. */
export async function saveBillingReview(
  input: UpsertClientBillingReviewInput,
  context: BillingReaderContext,
) {
  const submitted = UpsertClientBillingReviewSchema.strict().parse(
    structuredClone(input),
  )
  const period = month(submitted.period)
  if (submitted.dueDate != null && !isCalendarDate(submitted.dueDate))
    throw new BillingReadContractError()
  const row = await billingReviewApi.upsert(submitted, options(context))
  assertContext(context)
  assertReview(row, context, period)
  if (
    row.entityId !== submitted.entityId ||
    (submitted.status !== undefined && row.status !== submitted.status) ||
    (submitted.reviewerUserId !== undefined &&
      row.reviewerUserId !== submitted.reviewerUserId) ||
    (submitted.dueDate !== undefined && row.dueDate !== submitted.dueDate)
  )
    throw new BillingReadContractError()
  return { entityId: submitted.entityId, period }
}

export class BillingRunConfirmationError extends Error {
  constructor() {
    super(
      'Could not confirm the created draft invoices. Check this client’s invoices before submitting again.',
    )
    this.name = 'BillingRunConfirmationError'
  }
}

export class BillingRunInputError extends Error {
  constructor() {
    super(
      'Review the selected work, billing options and dates before creating invoices.',
    )
    this.name = 'BillingRunInputError'
  }
}

/** A writer acknowledgement supplies links/counts, never invoice reader bodies. */
export async function createClientBillingRun(
  entityId: string,
  input: CreateBillingRunPayload,
  context: BillingReaderContext,
): Promise<Omit<CreateBillingRunResponse, 'redirectUrl'>> {
  if (!isValidUUID(entityId)) throw new BillingRunInputError()
  const parsed = CreateBillingRunSchema.safeParse(structuredClone(input))
  if (!parsed.success) throw new BillingRunInputError()
  const command = parsed.data
  const result = await invoiceApi.createClientBillingRun(
    entityId,
    command,
    options(context),
  )
  assertContext(context)
  const entryCount = new Set(command.entryIds).size
  const expenseCount = new Set(command.expenseIds ?? []).size
  const amount = (value: unknown) =>
    typeof value === 'string' &&
    value.length <= 64 &&
    /^-?\d+\.\d{2}$/.test(value)
  const count = (value: unknown): value is number =>
    typeof value === 'number' &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= entryCount + expenseCount
  if (
    !result ||
    !Array.isArray(result.invoices) ||
    result.invoices.length === 0 ||
    result.invoices.length > entryCount + expenseCount ||
    result.entryCount !== entryCount ||
    result.expenseCount !== expenseCount ||
    typeof result.currency !== 'string' ||
    !/^[A-Z]{3}$/.test(result.currency) ||
    !amount(result.totalAmount) ||
    result.invoices.some(
      row =>
        !row ||
        !isValidUUID(row.invoiceId) ||
        typeof row.invoiceNumber !== 'string' ||
        !row.invoiceNumber.trim() ||
        row.invoiceNumber.length > 200 ||
        (row.projectId !== null && !isValidUUID(row.projectId)) ||
        (row.projectName !== null && typeof row.projectName !== 'string') ||
        !amount(row.totalAmount) ||
        !count(row.entryCount) ||
        !count(row.expenseCount) ||
        row.entryCount + row.expenseCount === 0,
    ) ||
    new Set(result.invoices.map(row => row.invoiceId)).size !==
      result.invoices.length ||
    result.invoices.reduce((sum, row) => sum + row.entryCount, 0) !==
      entryCount ||
    result.invoices.reduce((sum, row) => sum + row.expenseCount, 0) !==
      expenseCount ||
    (command.invoiceMode !== 'project' &&
      (result.invoices.length !== 1 || result.invoices[0]!.projectId !== null))
  )
    throw new BillingRunConfirmationError()
  // Sum exact decimal units, so a plausible-looking but inconsistent receipt
  // cannot send the user to an unrelated financial result.
  if (
    result.invoices.reduce(
      (sum, row) => sum + BigInt(row.totalAmount.replace('.', '')),
      0n,
    ) !== BigInt(result.totalAmount.replace('.', ''))
  )
    throw new BillingRunConfirmationError()
  return {
    invoices: result.invoices.map(row => ({
      invoiceId: row.invoiceId,
      invoiceNumber: row.invoiceNumber,
      projectId: row.projectId,
      projectName: row.projectName,
      totalAmount: row.totalAmount,
      entryCount: row.entryCount,
      expenseCount: row.expenseCount,
    })),
    totalAmount: result.totalAmount,
    currency: result.currency,
    entryCount: result.entryCount,
    expenseCount: result.expenseCount,
  }
}

/** A durable command reply proves ownership and links, never reader bodies or totals. */
export function confirmBillingRunOutcome(
  entityId: string,
  request: BillingRunRequest,
  context: Pick<BillingReaderContext, 'organizationId' | 'userId'>,
  value: unknown,
  action: 'create' | 'resolve',
): BillingRunOutcome {
  const proof = BillingRunOutcomeSchema.safeParse(value)
  if (
    !proof.success ||
    proof.data.entityId !== entityId ||
    proof.data.organizationId !== context.organizationId ||
    proof.data.userId !== context.userId ||
    request.expectedUserId !== context.userId ||
    proof.data.operationId !== request.operationId ||
    (action === 'create' && proof.data.outcome !== 'APPLIED') ||
    proof.data.invoiceIds.length >
      new Set(request.input.entryIds).size +
        new Set(request.input.expenseIds ?? []).size
  )
    throw new BillingRunConfirmationError()
  return proof.data
}
export async function executeBillingRunRequest(
  entityId: string,
  request: BillingRunRequest,
  context: BillingReaderContext,
  action: 'create' | 'resolve',
): Promise<BillingRunOutcome> {
  const captured = BillingRunRequestSchema.safeParse(structuredClone(request))
  if (
    !captured.success ||
    !isValidUUID(entityId) ||
    captured.data.expectedUserId !== context.userId
  )
    throw new BillingRunInputError()
  const config = options(context)
  const row = await invoiceApi.executeClientBillingRunRequest(
    entityId,
    captured.data,
    action,
    config,
  )
  assertContext(context)
  return confirmBillingRunOutcome(entityId, captured.data, context, row, action)
}
